import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download } from 'lucide-react';
import publications from '../data/publicationsData';
import ContactModal from '../components/ContactModal';
import SEO from '../components/SEO';

const PublicationDetail = () => {
  const { slug } = useParams();
  const pub = publications.find(p => p.id === slug);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!pub) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#2d2d51] mb-4">Publication Not Found</h1>
          <Link to="/resources/publications" className="text-lime font-semibold hover:underline">Back to Publications</Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="font-heading text-xl sm:text-2xl font-bold text-[#2d2d51] mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="font-heading text-lg sm:text-xl font-bold text-[#2d2d51] mt-6 mb-3">{trimmed.replace('### ', '')}</h3>;
      if (trimmed.startsWith('- ')) return <li key={i} className="text-gray-600 leading-relaxed ml-6 mb-2 list-disc">{trimmed.replace('- ', '')}</li>;
      return <p key={i} className="text-gray-600 leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="publication-detail-page">
      <SEO title={pub.title} description={pub.excerpt || pub.title} path={`/resources/publications/${pub.id}`} />
      <section className="relative bg-[#2d2d51] overflow-hidden text-white py-12 md:py-16">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/resources/publications" className="inline-flex items-center text-lime-300 hover:text-white mb-5 md:mb-6 transition-colors text-sm" data-testid="back-to-publications">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Publications
          </Link>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
            <span className="bg-lime/20 text-lime-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full border border-lime/30">{pub.type}</span>
            <span className="flex items-center gap-1 text-lime-300 text-xs sm:text-sm"><FileText className="h-4 w-4" /> {pub.pages}</span>
            <span className="text-lime-300 text-xs sm:text-sm">{pub.size}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-5 md:mb-6 leading-tight">{pub.title}</h1>
          <p className="text-sm sm:text-lg md:text-xl text-lime-100">{pub.description}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
        <img src={pub.image} alt={pub.title} className="w-full h-52 sm:h-64 md:h-96 object-cover rounded-2xl shadow-2xl" />
      </div>

      <article className="py-10 md:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent(pub.content)}
        </div>
      </article>

      {/* CTA */}
      <section className="bg-gray-50 py-10 md:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#2d2d51] mb-4">Need Personalized Guidance?</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">Talk to our trademark experts for customized advice on protecting your brand.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            data-testid="publication-cta-button"
            className="inline-flex items-center justify-center bg-lime hover:bg-lime-dark text-white font-bold py-2.5 px-6 sm:py-3 sm:px-8 rounded-xl shadow-lg transition-all duration-300 text-sm sm:text-base"
          >
            Schedule Free Consultation
          </button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Schedule Consultation" />
    </div>
  );
};

export default PublicationDetail;
